import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface CareLoadRow {
  organization_id: string;
  care_owner_id: string;
  owner_name: string;
  active_cases: number;
  aging_requests: number;
  avg_resolution_days: number | null;
  resolved_30d: number;
  burnout_risk_flag: boolean;
  load_tier: string;
}

export type CareLoadContext = {
  asOf: Date;
  caregivers: CareLoadRow[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric value to a finite number.
 * PostgREST serialises NUMERIC/BIGINT columns as strings; without coercion,
 * a single unparseable value poisons summary totals (NaN propagates through `+`,
 * and a string upstream value would coerce the whole sum to a concatenated string).
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Coerce truthiness of a Postgres BOOLEAN. PostgREST returns booleans as native
 * `true`/`false`, but legacy/seeded paths have produced the string `"false"`,
 * which is truthy in JS — so `if (flag)` would mis-classify EVERY row as at-risk.
 */
function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
}

/**
 * Format the avg-resolution-days display value. Null/NaN/non-finite all render
 * as "N/A" — the original `?? "N/A"` only caught null/undefined, so NaN slipped
 * through and rendered as the string "NaN" in printed reports. Finite values are
 * rounded to one decimal place to avoid `4.56789` artifacts.
 */
function formatAvgResolutionDays(value: number | null): number | string {
  if (value === null || value === undefined) return "N/A";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "N/A";
  return Math.round(n * 10) / 10;
}

/**
 * Normalize load_tier for stable downstream rendering — trimmed and lowercased
 * so the renderer's color-coding doesn't have to match every input case. Empty
 * or non-string falls back to "manageable" (the safe default already used by
 * the wiring layer for null upstream values).
 */
function normalizeLoadTier(value: unknown): string {
  if (typeof value !== "string") return "manageable";
  const trimmed = value.trim();
  if (trimmed.length === 0) return "manageable";
  return trimmed.toLowerCase();
}

/** Render owner name with fallback so a blank label never reaches the UI. */
function formatOwnerName(value: unknown): string {
  if (typeof value !== "string") return "Unknown Caregiver";
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "Unknown Caregiver";
}

export function buildCareLoadAggregation(
  context: CareLoadContext,
): ReportAggregation {
  const caregivers = Array.isArray(context.caregivers) ? context.caregivers : [];

  const reportRows = caregivers.map((cg) => {
    const activeCases = toFiniteNumber(cg.active_cases);
    const agingRequests = toFiniteNumber(cg.aging_requests);
    const resolved30d = toFiniteNumber(cg.resolved_30d);
    const isAtRisk = toBoolean(cg.burnout_risk_flag);
    return {
      id: cg.care_owner_id,
      label: formatOwnerName(cg.owner_name),
      entityType: "person" as const,
      drilldownUrl: `/people/${encodeURIComponent(cg.care_owner_id)}`,
      activeCases,
      agingRequests,
      avgResolutionDays: formatAvgResolutionDays(cg.avg_resolution_days),
      resolved30d,
      loadTier: normalizeLoadTier(cg.load_tier),
      burnoutRisk: isAtRisk ? "At Risk" : "OK",
    };
  });

  // Stable, locale-independent sort: at-risk caregivers first (so they jump
  // to the top for the user), then by descending active_cases (heaviest load
  // first), then by owner name as a deterministic tiebreaker. We use `<`/`>`
  // rather than `localeCompare` so order is identical across locales (#8).
  reportRows.sort((a, b) => {
    const aRisk = a.burnoutRisk === "At Risk" ? 1 : 0;
    const bRisk = b.burnoutRisk === "At Risk" ? 1 : 0;
    if (aRisk !== bRisk) return bRisk - aRisk;
    if (a.activeCases !== b.activeCases) {
      return b.activeCases - a.activeCases;
    }
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });

  // Single-pass aggregation. `active_cases` goes through `toFiniteNumber` so a
  // single NaN or string-typed numeric upstream doesn't poison the total
  // (without it, mixing strings would `+` to a concatenated string like
  // "05NaN3"). `burnout_risk_flag` goes through `toBoolean` so a string
  // "false" doesn't get mis-counted as truthy.
  let totalActive = 0;
  let atRiskCount = 0;
  for (const cg of caregivers) {
    totalActive += toFiniteNumber(cg.active_cases);
    if (toBoolean(cg.burnout_risk_flag)) atRiskCount += 1;
  }

  return {
    asOf: context.asOf,
    summary: [
      { label: "Care Team Size", value: caregivers.length },
      { label: "Active Cases", value: totalActive },
      { label: "At Burnout Risk", value: atRiskCount },
    ],
    rows: reportRows,
  };
}
